import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

/**
 * Classes for the scroll container that fills the card — one per route, owning
 * vertical scroll (html/body are locked; see styles/app.css). `relative`
 * keeps react-aria's absolutely-
 * positioned visually-hidden inputs inside the scroller; `[overflow-anchor:
 * none]` keeps browser scroll anchoring from walking `scrollTop` when content
 * above the viewport changes height (a collapsing account section, a loading
 * chart). Shared between `HomeScene`'s route scroller and the Storybook
 * `paneCardDecorator` so the two contexts cannot drift.
 */
export const PANE_SCROLLER_CLASSNAME =
  'relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden [overflow-anchor:none]';

interface IPaneCardProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * The rounded surface every routed pane sits on (Figma "Context" container,
 * node `8006:877966` in Mantle — UX/UI V2): `bg-app_light` fill, hairline
 * `border-secondary_alt`, `rounded-2xl`, `shadow-xl`, clipped content. The
 * card's edges stay pinned while the pane scrolls inside it, so the scroller
 * belongs to the children, not to this component.
 *
 * `HomeScene` renders it around the route scroller; pane-level Storybook
 * stories reproduce the same context via `paneCardDecorator`, so a pane never
 * renders this itself.
 */
const PaneCard = ({ children, className }: IPaneCardProps) => {
  return (
    <div
      className={cx(
        'border-secondary_alt bg-app_light flex min-h-0 w-full flex-1 flex-col overflow-clip rounded-2xl border shadow-xl',
        className
      )}
    >
      {children}
    </div>
  );
};

export default PaneCard;
